import React, { useState, useEffect, useContext, useCallback } from "react";
import styled from "styled-components";
import { message } from "antd";
import { AuthContext } from "../context/AuthProvider";
import { getApiUrl } from "../lib/api/config";
import { getAuth } from "firebase/auth";
import {
  readUrlHadNonemptySecret,
  consumeOpsSecretFromUrl,
} from "../lib/opsUrlSecret";

const REFRESH_INTERVAL = 15000; // 15 seconds for real-time feel

const OpsDashboardEnhanced = () => {
  const { user, userInfo } = useContext(AuthContext);
  const [urlHadSecretOnMount] = useState(() => readUrlHadNonemptySecret());
  const [opsAppSecret] = useState(() => consumeOpsSecretFromUrl());
  /** Firebase Auth custom claims (admin) - not the Firestore users/{uid} document */
  const [authClaims, setAuthClaims] = useState({ admin: false, pending: true });
  const [data, setData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [activeChatSessions, setActiveChatSessions] = useState([]);
  const apiUrl = getApiUrl();

  useEffect(() => {
    if (!user?.uid) {
      setAuthClaims({ admin: false, pending: false });
      return;
    }
    let cancelled = false;
    setAuthClaims((s) => ({ ...s, pending: true }));
    const cu = getAuth().currentUser;
    if (!cu) {
      setAuthClaims({ admin: false, pending: false });
      return;
    }
    cu.getIdTokenResult()
      .then((r) => {
        if (!cancelled) {
          setAuthClaims({
            admin: r?.claims?.admin === true,
            pending: false,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setAuthClaims({ admin: false, pending: false });
      });
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  const isAdmin =
    authClaims.admin === true || userInfo?.customClaims?.admin === true;
  const isUiUnlocked = urlHadSecretOnMount || isAdmin;
  const isAuthorized = isUiUnlocked && (Boolean(opsAppSecret) || isAdmin);

  const buildOpsAuthHeaders = useCallback(async () => {
    const headers = { "Content-Type": "application/json" };
    if (opsAppSecret) {
      headers["X-App-Secret"] = opsAppSecret;
      return headers;
    }
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("No Firebase session");
    }
    headers["Authorization"] = `Bearer ${token}`;
    return headers;
  }, [opsAppSecret]);

  const fetchHealthStatus = async () => {
    try {
      const headers = await buildOpsAuthHeaders();
      const response = await fetch(`${apiUrl}/ops-dashboard/health`, {
        headers,
      });

      if (response.ok) {
        const result = await response.json();
        setHealthData(result);
      }
    } catch (e) {
      console.error("Health check failed:", e);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const headers = await buildOpsAuthHeaders();
      const response = await fetch(`${apiUrl}/ops-dashboard/enhanced-data`, {
        headers,
      });

      if (!response.ok) {
        const errText = await response.text().catch(() => "");
        console.error("[MissionControl] enhanced-data failed", {
          status: response.status,
          url: response.url,
          body: errText?.slice(0, 500),
        });
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      if (!result.ok) {
        throw new Error(result.error || "Unknown error");
      }

      setData(result);
      setActiveChatSessions(result.activeChatSessions || []);
      setError(null);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionDetails = async (sessionId) => {
    try {
      const headers = await buildOpsAuthHeaders();
      const response = await fetch(
        `${apiUrl}/ops-dashboard/session/${sessionId}`,
        {
          headers,
        }
      );

      if (response.ok) {
        const sessionData = await response.json();
        setSelectedSession(sessionData);
      }
    } catch (e) {
      message.error("Failed to load session details");
    }
  };

  const triggerManualIntervention = async (jobId, action) => {
    try {
      const headers = await buildOpsAuthHeaders();
      const response = await fetch(
        `${apiUrl}/ops-dashboard/trigger-followup/${jobId}`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ action }),
        }
      );

      const result = await response.json();
      if (result.ok) {
        message.success(`Manual intervention triggered for job ${jobId}`);
        fetchDashboardData();
      } else {
        message.error(`Error: ${result.error}`);
      }
    } catch (e) {
      message.error(`Error triggering intervention: ${e.message}`);
    }
  };

  useEffect(() => {
    if (!isAuthorized) return;
    if (!opsAppSecret && user?.uid && authClaims.pending) return;
    fetchHealthStatus();
    fetchDashboardData();
  }, [user?.uid, isAuthorized, opsAppSecret, authClaims.pending]);

  // Auto-refresh
  useEffect(() => {
    if (!data || !isAuthorized) return;

    const interval = setInterval(() => {
      fetchHealthStatus();
      fetchDashboardData();
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [data, isAuthorized]);

  // No ?secret= on first paint: wait for Firebase JWT to see if user is admin
  if (!urlHadSecretOnMount && user?.uid && authClaims.pending) {
    return (
      <Container>
        <SecurityGate>
          <GateTitle>VERIFYING ACCESS</GateTitle>
          <GateMessage>Checking admin privileges…</GateMessage>
        </SecurityGate>
      </Container>
    );
  }

  if (!isUiUnlocked) {
    return (
      <Container>
        <SecurityGate>
          <GateLock aria-hidden>🔒</GateLock>
          <GateTitle>MISSION CONTROL LOCKED</GateTitle>
          <GateMessage>
            Load this URL with <code>?secret=</code> and your host{" "}
            <strong>APP_SECRET</strong> on the first visit. The query is
            stripped after load. Firebase users with the{" "}
            <strong>admin: true</strong> custom claim may open Mission Control
            without the query param.
          </GateMessage>
          <GateCode>
            {typeof window !== "undefined"
              ? `${window.location.origin}${window.location.pathname}?secret=YOUR_APP_SECRET`
              : "/ops-legacy?secret=YOUR_APP_SECRET"}
          </GateCode>
        </SecurityGate>
      </Container>
    );
  }

  if (!isAuthorized) {
    return (
      <Container>
        <SecurityGate>
          <GateTitle>MISSION CONTROL ACCESS</GateTitle>
          <GateMessage>
            Open with your Netlify <strong>APP_SECRET</strong> (the{" "}
            <code>?secret=</code> value is removed from the address bar after
            load and kept only for this browser session):
          </GateMessage>
          <GateCode>
            {typeof window !== "undefined"
              ? `${window.location.origin}/ops?secret=YOUR_APP_SECRET`
              : "/ops?secret=YOUR_APP_SECRET"}
          </GateCode>
          <GateMessage style={{ marginTop: "1rem" }}>
            Or sign in with a Firebase user that has the{" "}
            <strong>admin: true</strong> custom claim.
          </GateMessage>
          {user?.uid && !authClaims.pending ? (
            <GateDetails style={{ marginTop: "1rem" }}>
              Signed in as {user.uid}
              <br />
              Admin claim (Auth JWT): {authClaims.admin ? "TRUE" : "FALSE"}
            </GateDetails>
          ) : null}
        </SecurityGate>
      </Container>
    );
  }

  if (loading && !data) {
    return (
      <Container>
        <Header>
          <SystemTitle>CLUMSE MISSION CONTROL</SystemTitle>
          <SystemStatus>INITIALIZING...</SystemStatus>
        </Header>
        <LoadingMessage>Establishing secure connection...</LoadingMessage>
      </Container>
    );
  }

  if (error && !data) {
    return (
      <Container>
        <Header>
          <SystemTitle>CLUMSE MISSION CONTROL</SystemTitle>
          <SystemStatus error>CONNECTION ERROR</SystemStatus>
        </Header>
        <ErrorMessage>
          <ErrorCode>{error}</ErrorCode>
          <RetryButton onClick={fetchDashboardData}>RECONNECT</RetryButton>
        </ErrorMessage>
      </Container>
    );
  }

  if (!data) return null;

  const formatCurrency = (amount) => `$${amount.toLocaleString()}`;
  const formatDuration = (ms) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const mins = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${mins}m`;
  };

  return (
    <Container>
      <Header>
        <SystemTitle>CLUMSE MISSION CONTROL</SystemTitle>
        <HeaderMeta>
          <StatusIndicator operational={!error}>
            ● {error ? "DEGRADED" : "OPERATIONAL"}
          </StatusIndicator>
          <Timestamp>
            {lastRefresh?.toLocaleTimeString() || "NEVER"}
            <RefreshButton
              onClick={() => {
                fetchHealthStatus();
                fetchDashboardData();
              }}
            >
              ↻
            </RefreshButton>
          </Timestamp>
        </HeaderMeta>
      </Header>

      {/* Preflight Health System */}
      <PreflightBar>
        <PreflightTitle>PREFLIGHT STATUS</PreflightTitle>
        <PreflightGrid>
          <HealthMetric
            status={healthData?.firestore ? "operational" : "degraded"}
          >
            <MetricLabel>FIRESTORE</MetricLabel>
            <MetricStatus>
              {healthData?.firestore ? "ONLINE" : "DEGRADED"}
            </MetricStatus>
            {healthData?.firestoreLatency && (
              <MetricLatency>{healthData.firestoreLatency}ms</MetricLatency>
            )}
          </HealthMetric>
          <HealthMetric
            status={healthData?.aiGateway ? "operational" : "degraded"}
          >
            <MetricLabel>AI GATEWAY</MetricLabel>
            <MetricStatus>
              {healthData?.aiGateway ? "ONLINE" : "DEGRADED"}
            </MetricStatus>
          </HealthMetric>
          <HealthMetric
            status={healthData?.messaging ? "operational" : "degraded"}
          >
            <MetricLabel>MESSAGING</MetricLabel>
            <MetricStatus>
              {healthData?.messaging ? "ONLINE" : "DEGRADED"}
            </MetricStatus>
          </HealthMetric>
          <HealthMetric
            status={healthData?.payments ? "operational" : "degraded"}
          >
            <MetricLabel>PAYMENTS</MetricLabel>
            <MetricStatus>
              {healthData?.payments ? "ONLINE" : "DEGRADED"}
            </MetricStatus>
          </HealthMetric>
        </PreflightGrid>
      </PreflightBar>

      {data.firestoreIntel ? (
        <FirestoreIntelSection>
          <SectionHeader>
            <SectionTitle>FIRESTORE DATA AND INDEXES</SectionTitle>
            <LiveIndicator style={{ opacity: 0.7 }}>CATALOG</LiveIndicator>
          </SectionHeader>
          <FirestoreIntelGrid>
            <FirestorePanel>
              <PanelTitle>Root collections (approx. counts)</PanelTitle>
              <PanelHint>
                Merges live <code>listCollections()</code> with a curated Clumse
                list. Counts use Firestore aggregation (fast; not a full scan).
              </PanelHint>
              <CatalogTable>
                <CatalogHead>
                  <span>Collection</span>
                  <span>~Docs</span>
                  <span>Source</span>
                </CatalogHead>
                {(data.firestoreIntel.dataCatalog || []).map((row) => (
                  <CatalogRow key={row.collectionId}>
                    <CatalogCell mono title={row.collectionId}>
                      {row.collectionId}
                    </CatalogCell>
                    <CatalogCell>
                      {row.error ? (
                        <CatalogErr title={row.error}>err</CatalogErr>
                      ) : (
                        (row.approximateCount ?? 0).toLocaleString()
                      )}
                    </CatalogCell>
                    <CatalogCell dim>{row.source}</CatalogCell>
                  </CatalogRow>
                ))}
              </CatalogTable>
            </FirestorePanel>

            <FirestorePanel>
              <PanelTitle>Composite indexes (repo vs project)</PanelTitle>
              <PanelHint>
                {data.firestoreIntel.indexManifest?.ok
                  ? `Manifest: ${data.firestoreIntel.indexManifest.sourcePath} · ${data.firestoreIntel.indexManifest.compositeCount} definitions · ${data.firestoreIntel.indexManifest.collectionGroups.length} collection groups`
                  : `Manifest: ${
                      data.firestoreIntel.indexManifest?.reason || "unavailable"
                    }`}
              </PanelHint>
              {data.firestoreIntel.indexDeployCheck?.remoteError ? (
                <IndexAlert warn>
                  Remote compare:{" "}
                  {data.firestoreIntel.indexDeployCheck.remoteError}
                </IndexAlert>
              ) : null}
              <IndexStatusRow>
                <IndexPill $ok={data.firestoreIntel.indexDeployCheck?.ok}>
                  {data.firestoreIntel.indexDeployCheck?.ok
                    ? "Clumse-relevant repo indexes READY on project"
                    : `${
                        (
                          data.firestoreIntel.indexDeployCheck
                            ?.missingOrNotReadyRelevant || []
                        ).length
                      } Clumse-relevant index(es) not READY`}
                </IndexPill>
                {(data.firestoreIntel.indexDeployCheck?.buildingOnRemote || [])
                  .length > 0 ? (
                  <IndexPill $ok={false}>
                    {
                      (
                        data.firestoreIntel.indexDeployCheck
                          ?.buildingOnRemote || []
                      ).length
                    }{" "}
                    building
                  </IndexPill>
                ) : null}
                <IndexPill $neutral>
                  {(data.firestoreIntel.indexDeployCheck?.onlyOnRemoteCount ??
                    0) + ""}{" "}
                  READY on project only (not in repo file)
                </IndexPill>
                {(data.firestoreIntel.indexDeployCheck
                  ?.missingOtherRepoDefinitions ?? 0) > 0 ? (
                  <IndexPill $neutral>
                    {(data.firestoreIntel.indexDeployCheck
                      ?.missingOtherRepoDefinitions ?? 0) + ""}{" "}
                    other repo definitions (legacy / unused groups)
                  </IndexPill>
                ) : null}
              </IndexStatusRow>
              <PanelSubTitle>
                Gaps for this project (live root collections plus Clumse curated
                list)
              </PanelSubTitle>
              <IndexList>
                {(
                  data.firestoreIntel.indexDeployCheck
                    ?.missingOrNotReadyRelevant || []
                ).length === 0 ? (
                  <IndexListItem $muted>None detected.</IndexListItem>
                ) : (
                  (
                    data.firestoreIntel.indexDeployCheck
                      ?.missingOrNotReadyRelevant || []
                  ).map((m) => (
                    <IndexListItem key={m.signature}>
                      <strong>{m.collectionGroup}</strong>
                      <span>{m.summary}</span>
                    </IndexListItem>
                  ))
                )}
              </IndexList>
              <PanelSubTitle>
                All definitions in firestore.indexes.json missing on project
                (full list)
              </PanelSubTitle>
              <IndexList>
                {(data.firestoreIntel.indexDeployCheck?.missingOrNotReady || [])
                  .length === 0 ? (
                  <IndexListItem $muted>
                    None detected (or remote compare unavailable).
                  </IndexListItem>
                ) : (
                  (
                    data.firestoreIntel.indexDeployCheck?.missingOrNotReady ||
                    []
                  ).map((m) => (
                    <IndexListItem key={m.signature}>
                      <strong>{m.collectionGroup}</strong>
                      <span>{m.summary}</span>
                    </IndexListItem>
                  ))
                )}
              </IndexList>
              {(data.firestoreIntel.indexDeployCheck?.buildingOnRemote || [])
                .length > 0 ? (
                <>
                  <PanelSubTitle>Still building on Firebase</PanelSubTitle>
                  <IndexList>
                    {(
                      data.firestoreIntel.indexDeployCheck?.buildingOnRemote ||
                      []
                    ).map((b) => (
                      <IndexListItem key={b.signature}>
                        <strong>{b.collectionGroup}</strong>
                        <span>{b.state}</span>
                      </IndexListItem>
                    ))}
                  </IndexList>
                </>
              ) : null}
              {(data.firestoreIntel.indexDeployCheck?.onlyOnRemoteSample || [])
                .length > 0 ? (
                <>
                  <PanelSubTitle>
                    Sample: READY on project but not in repo file (console or
                    legacy)
                  </PanelSubTitle>
                  <IndexList>
                    {(
                      data.firestoreIntel.indexDeployCheck
                        ?.onlyOnRemoteSample || []
                    ).map((o) => (
                      <IndexListItem key={o.signature} $muted>
                        <span title={o.signature}>{o.collectionGroup}</span>
                      </IndexListItem>
                    ))}
                  </IndexList>
                </>
              ) : null}
              <IndexHint>
                {data.firestoreIntel.indexDeployCheck?.hint}
              </IndexHint>
            </FirestorePanel>
          </FirestoreIntelGrid>
        </FirestoreIntelSection>
      ) : null}

      <MainGrid>
        {/* Mission Control Feed */}
        <MissionControlSection span={selectedSession ? 1 : 2}>
          <SectionHeader>
            <SectionTitle>MISSION CONTROL FEED</SectionTitle>
            <LiveIndicator>● LIVE</LiveIndicator>
          </SectionHeader>

          <ChatSessionsTable>
            <TableHeader>
              <HeaderCell>USER</HeaderCell>
              <HeaderCell>LOCATION</HeaderCell>
              <HeaderCell>COMPLETION</HeaderCell>
              <HeaderCell>TIER</HeaderCell>
              <HeaderCell>ACTIVITY</HeaderCell>
              <HeaderCell>ACTIONS</HeaderCell>
            </TableHeader>
            <TableBody>
              {activeChatSessions.map((session) => (
                <SessionRow
                  key={session.id}
                  onClick={() => fetchSessionDetails(session.id)}
                  selected={selectedSession?.id === session.id}
                >
                  <DataCell>
                    <UserInfo>
                      <UserId>{session.userId?.slice(-8) || "Unknown"}</UserId>
                      <UserStatus
                        active={session.lastActive > Date.now() - 300000}
                      >
                        {session.lastActive > Date.now() - 300000
                          ? "ACTIVE"
                          : "IDLE"}
                      </UserStatus>
                    </UserInfo>
                  </DataCell>
                  <DataCell>
                    <LocationData>{session.zipCode || "N/A"}</LocationData>
                  </DataCell>
                  <DataCell>
                    <CompletionMeter value={session.completion || 0}>
                      <MeterFill width={session.completion || 0} />
                      <MeterText>{session.completion || 0}%</MeterText>
                    </CompletionMeter>
                  </DataCell>
                  <DataCell>
                    <FeeTier tier={session.feeTier}>
                      ${session.feeTier || "TBD"}
                    </FeeTier>
                  </DataCell>
                  <DataCell>
                    <ActivityTime>
                      {session.lastActive
                        ? formatDuration(Date.now() - session.lastActive)
                        : "N/A"}
                    </ActivityTime>
                  </DataCell>
                  <DataCell>
                    {session.jobId && (
                      <ActionButton
                        onClick={(e) => {
                          e.stopPropagation();
                          triggerManualIntervention(
                            session.jobId,
                            "priority_escalation"
                          );
                        }}
                        urgent={
                          session.completion >= 100 && !session.dispatched
                        }
                      >
                        ESCALATE
                      </ActionButton>
                    )}
                  </DataCell>
                </SessionRow>
              ))}
            </TableBody>
          </ChatSessionsTable>
        </MissionControlSection>

        {/* Deep Dive Pane */}
        {selectedSession && (
          <DeepDiveSection>
            <SectionHeader>
              <SectionTitle>
                DEEP DIVE: {selectedSession.userId?.slice(-8)}
              </SectionTitle>
              <CloseButton onClick={() => setSelectedSession(null)}>
                ✕
              </CloseButton>
            </SectionHeader>

            <DeepDiveContent>
              <TranscriptPane>
                <PaneTitle>USER ↔ CLUMSE AI TRANSCRIPT</PaneTitle>
                <TranscriptMessages>
                  {selectedSession.transcript?.map((msg, idx) => (
                    <TranscriptMessage key={idx} role={msg.role}>
                      <MessageMeta>
                        <MessageRole>{msg.role.toUpperCase()}</MessageRole>
                        <MessageTime>
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </MessageTime>
                      </MessageMeta>
                      <MessageContent>{msg.content}</MessageContent>
                    </TranscriptMessage>
                  ))}
                </TranscriptMessages>
              </TranscriptPane>

              <VendorPane>
                <PaneTitle>CONTRACTOR COMMUNICATIONS</PaneTitle>
                <VendorLogs>
                  {selectedSession.vendorLogs?.map((log, idx) => {
                    const isPortalActivity = log.action?.includes("PORTAL");
                    return (
                      <VendorLogEntry key={idx} isPortal={isPortalActivity}>
                        <LogTime>
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </LogTime>
                        <LogAction isPortal={isPortalActivity}>
                          {log.action}
                          {isPortalActivity && (
                            <PortalBadge>PORTAL</PortalBadge>
                          )}
                        </LogAction>
                        <LogDetails>{log.details}</LogDetails>
                      </VendorLogEntry>
                    );
                  })}
                </VendorLogs>
              </VendorPane>
            </DeepDiveContent>
          </DeepDiveSection>
        )}

        {/* Revenue Engine - Existing */}
        <MetricsSection>
          <SectionTitle>REVENUE ENGINE</SectionTitle>
          {data.revenue?.error ? (
            <ErrorText>{data.revenue.error}</ErrorText>
          ) : (
            <>
              <MetricRow>
                <MetricLabel>24h:</MetricLabel>
                <MetricValue success>
                  {formatCurrency(data.revenue.last24h)}
                </MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>7d:</MetricLabel>
                <MetricValue success>
                  {formatCurrency(data.revenue.last7d)}
                </MetricValue>
              </MetricRow>
              <MetricRow>
                <MetricLabel>30d:</MetricLabel>
                <MetricValue success>
                  {formatCurrency(data.revenue.last30d)}
                </MetricValue>
              </MetricRow>
              <FeeBreakdown>
                $25: {data.revenue.fees.collected25} | $95:{" "}
                {data.revenue.fees.collected95} | $295:{" "}
                {data.revenue.fees.collected295}
              </FeeBreakdown>
            </>
          )}
        </MetricsSection>

        {/* Shark Status - Enhanced */}
        <MetricsSection>
          <SectionTitle>
            SHARK TRACKER
            <StatusIndicator operational={data.sharkStatus?.activeSearches > 0}>
              {data.sharkStatus?.activeSearches > 0 ? "● HUNTING" : "○ IDLE"}
            </StatusIndicator>
          </SectionTitle>
          {data.sharkStatus?.error ? (
            <ErrorText>{data.sharkStatus.error}</ErrorText>
          ) : (
            <>
              <MetricRow>
                <MetricLabel>Active searches:</MetricLabel>
                <MetricValue warning={data.sharkStatus.activeSearches > 10}>
                  {data.sharkStatus.activeSearches}
                </MetricValue>
              </MetricRow>
              <JobList>
                {data.sharkStatus.searchingJobs
                  ?.filter((job) => job.bidCount < 5)
                  .map((job) => (
                    <JobItem key={job.id} urgent={job.hoursSinceDispatch > 4}>
                      <JobDescription>{job.description}</JobDescription>
                      <JobMeta>
                        <JobBadge>{job.zipCode}</JobBadge>
                        <JobBadge urgent={job.hoursSinceDispatch > 4}>
                          {job.hoursSinceDispatch}h
                        </JobBadge>
                        <JobBadge error={job.bidCount < 5}>
                          {job.bidCount}/5 bids
                        </JobBadge>
                        <InterventionButton
                          onClick={() =>
                            triggerManualIntervention(job.id, "shark_boost")
                          }
                        >
                          BOOST
                        </InterventionButton>
                      </JobMeta>
                    </JobItem>
                  ))}
              </JobList>
            </>
          )}
        </MetricsSection>
      </MainGrid>
    </Container>
  );
};

// Enhanced Styled Components with Technical Diplomacy aesthetic
const Container = styled.div`
  font-family:
    "Inter",
    -apple-system,
    BlinkMacSystemFont,
    sans-serif;
  background: linear-gradient(180deg, #0a0f1c 0%, #1a1f2e 100%);
  color: #e2e8f0;
  min-height: 100vh;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 20px 0;
  border-bottom: 1px solid #2d3748;
`;

const SystemTitle = styled.h1`
  font-family: "IBM Plex Mono", "SF Mono", Consolas, monospace;
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  letter-spacing: 2px;
  text-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
`;

const SystemStatus = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 12px;
  color: ${(props) => (props.error ? "#ef4444" : "#10b981")};
  font-weight: 600;
  letter-spacing: 1px;
`;

const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const StatusIndicator = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 12px;
  color: ${(props) => (props.operational ? "#10b981" : "#ef4444")};
  font-weight: 600;
  animation: ${(props) => (props.operational ? "pulse 2s infinite" : "none")};

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.7;
    }
  }
`;

const Timestamp = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 11px;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RefreshButton = styled.button`
  background: #1e293b;
  border: 1px solid #334155;
  color: #e2e8f0;
  padding: 4px 8px;
  cursor: pointer;
  font-family: "IBM Plex Mono", monospace;
  font-size: 12px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #334155;
    border-color: #3b82f6;
  }
`;

const PreflightBar = styled.div`
  background: linear-gradient(90deg, #1e293b 0%, #334155 100%);
  border: 1px solid #475569;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
`;

const PreflightTitle = styled.h3`
  font-family: "IBM Plex Mono", monospace;
  font-size: 14px;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 12px 0;
  letter-spacing: 1px;
`;

const PreflightGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
`;

const HealthMetric = styled.div`
  background: ${(props) =>
    props.status === "operational"
      ? "rgba(16, 185, 129, 0.1)"
      : "rgba(239, 68, 68, 0.1)"};
  border: 1px solid
    ${(props) => (props.status === "operational" ? "#10b981" : "#ef4444")};
  border-radius: 6px;
  padding: 12px;
  text-align: center;
`;

const MetricLabel = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  color: #94a3b8;
  margin-bottom: 4px;
  letter-spacing: 0.5px;
`;

const MetricStatus = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 12px;
  font-weight: 700;
  color: #f1f5f9;
`;

const MetricLatency = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  color: #64748b;
  margin-top: 2px;
`;

const FirestoreIntelSection = styled.div`
  background: linear-gradient(145deg, #0f172a 0%, #1e293b 100%);
  border: 1px solid #475569;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
`;

const FirestoreIntelGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const FirestorePanel = styled.div`
  background: rgba(15, 23, 42, 0.6);
  border: 1px solid #334155;
  border-radius: 8px;
  padding: 14px;
  min-height: 120px;
`;

const PanelTitle = styled.h4`
  font-family: "IBM Plex Mono", monospace;
  font-size: 12px;
  font-weight: 700;
  color: #e2e8f0;
  margin: 0 0 8px 0;
  letter-spacing: 0.5px;
`;

const PanelSubTitle = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  font-weight: 600;
  color: #94a3b8;
  margin: 12px 0 6px 0;
`;

const PanelHint = styled.div`
  font-size: 10px;
  color: #64748b;
  line-height: 1.4;
  margin-bottom: 10px;

  code {
    font-size: 9px;
    color: #cbd5e1;
  }
`;

const CatalogTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 320px;
  overflow-y: auto;
  font-size: 10px;
`;

const CatalogHead = styled.div`
  display: grid;
  grid-template-columns: 1fr 72px 72px;
  gap: 6px;
  padding: 6px 8px;
  color: #64748b;
  font-family: "IBM Plex Mono", monospace;
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #334155;
  position: sticky;
  top: 0;
  background: rgba(15, 23, 42, 0.95);
  z-index: 1;
`;

const CatalogRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 72px 72px;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 4px;
  &:nth-child(even) {
    background: rgba(30, 41, 59, 0.35);
  }
`;

const CatalogCell = styled.span`
  color: ${(p) => (p.dim ? "#64748b" : "#e2e8f0")};
  font-family: ${(p) => (p.mono ? "'IBM Plex Mono', monospace" : "inherit")};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CatalogErr = styled.span`
  color: #f87171;
  cursor: help;
`;

const IndexStatusRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

const IndexPill = styled.span`
  font-family: "IBM Plex Mono", monospace;
  font-size: 9px;
  font-weight: 700;
  padding: 4px 8px;
  border-radius: 999px;
  letter-spacing: 0.3px;
  background: ${(p) =>
    p.$neutral
      ? "rgba(100, 116, 139, 0.25)"
      : p.$ok
      ? "rgba(16, 185, 129, 0.15)"
      : "rgba(239, 68, 68, 0.15)"};
  border: 1px solid
    ${(p) =>
      p.$neutral
        ? "#475569"
        : p.$ok
        ? "rgba(16, 185, 129, 0.45)"
        : "rgba(239, 68, 68, 0.45)"};
  color: ${(p) => (p.$neutral ? "#cbd5e1" : p.$ok ? "#6ee7b7" : "#fca5a5")};
`;

const IndexList = styled.ul`
  margin: 0;
  padding-left: 16px;
  max-height: 200px;
  overflow-y: auto;
`;

const IndexListItem = styled.li`
  font-size: 10px;
  color: ${(p) => (p.$muted ? "#94a3b8" : "#e2e8f0")};
  margin-bottom: 6px;
  line-height: 1.35;

  strong {
    color: #f1f5f9;
    margin-right: 6px;
  }

  span {
    color: #94a3b8;
  }
`;

const IndexAlert = styled.div`
  font-size: 10px;
  color: ${(p) => (p.warn ? "#fbbf24" : "#94a3b8")};
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.25);
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 10px;
  line-height: 1.4;
`;

const IndexHint = styled.div`
  font-size: 9px;
  color: #64748b;
  line-height: 1.45;
  margin-top: 10px;
  border-top: 1px solid #334155;
  padding-top: 8px;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 24px;
`;

const MissionControlSection = styled.div`
  grid-column: span ${(props) => props.span || 1};
  background: linear-gradient(145deg, #1e293b 0%, #334155 100%);
  border: 1px solid #475569;
  border-radius: 8px;
  padding: 20px;
`;

const DeepDiveSection = styled.div`
  background: linear-gradient(145deg, #0f172a 0%, #1e293b 100%);
  border: 1px solid #475569;
  border-radius: 8px;
  padding: 20px;
  min-height: 600px;
`;

const MetricsSection = styled.div`
  background: linear-gradient(145deg, #1e293b 0%, #334155 100%);
  border: 1px solid #475569;
  border-radius: 8px;
  padding: 20px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h2`
  font-family: "IBM Plex Mono", monospace;
  font-size: 14px;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LiveIndicator = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  color: #ef4444;
  animation: pulse 1.5s infinite;
`;

const CloseButton = styled.button`
  background: transparent;
  border: 1px solid #475569;
  color: #94a3b8;
  padding: 4px 8px;
  cursor: pointer;
  font-family: "IBM Plex Mono", monospace;
  border-radius: 4px;

  &:hover {
    border-color: #ef4444;
    color: #ef4444;
  }
`;

const ChatSessionsTable = styled.div`
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid #334155;
  border-radius: 6px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px 100px 60px 80px 80px;
  background: #0f172a;
  border-bottom: 1px solid #334155;
`;

const HeaderCell = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  padding: 12px 8px;
  text-align: left;
  letter-spacing: 0.5px;
`;

const TableBody = styled.div`
  max-height: 400px;
  overflow-y: auto;
`;

const SessionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 80px 100px 60px 80px 80px;
  background: ${(props) =>
    props.selected ? "rgba(59, 130, 246, 0.1)" : "transparent"};
  border-bottom: 1px solid #1e293b;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(59, 130, 246, 0.05);
  }
`;

const DataCell = styled.div`
  padding: 12px 8px;
  display: flex;
  align-items: center;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const UserId = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 11px;
  color: #e2e8f0;
`;

const UserStatus = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 9px;
  color: ${(props) => (props.active ? "#10b981" : "#64748b")};
  font-weight: 600;
`;

const LocationData = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 11px;
  color: #94a3b8;
`;

const CompletionMeter = styled.div`
  position: relative;
  width: 80px;
  height: 16px;
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 8px;
  overflow: hidden;
`;

const MeterFill = styled.div`
  height: 100%;
  width: ${(props) => Math.min(props.width, 100)}%;
  background: linear-gradient(90deg, #ef4444 0%, #f59e0b 50%, #10b981 100%);
  transition: width 0.3s ease;
`;

const MeterText = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "IBM Plex Mono", monospace;
  font-size: 9px;
  color: #f1f5f9;
  font-weight: 700;
  text-shadow: 0 0 4px rgba(0, 0, 0, 0.8);
`;

const FeeTier = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 11px;
  font-weight: 700;
  color: ${(props) => {
    if (props.tier === 295) return "#ef4444";
    if (props.tier === 95) return "#f59e0b";
    if (props.tier === 25) return "#10b981";
    return "#64748b";
  }};
`;

const ActivityTime = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  color: #64748b;
`;

const ActionButton = styled.button`
  font-family: "IBM Plex Mono", monospace;
  font-size: 9px;
  padding: 4px 8px;
  background: ${(props) => (props.urgent ? "#ef4444" : "#3b82f6")};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  animation: ${(props) => (props.urgent ? "pulse 2s infinite" : "none")};

  &:hover {
    opacity: 0.8;
  }
`;

const DeepDiveContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  height: calc(100% - 60px);
`;

const TranscriptPane = styled.div`
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid #334155;
  border-radius: 6px;
  padding: 12px;
  display: flex;
  flex-direction: column;
`;

const VendorPane = styled.div`
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid #334155;
  border-radius: 6px;
  padding: 12px;
  display: flex;
  flex-direction: column;
`;

const PaneTitle = styled.h4`
  font-family: "IBM Plex Mono", monospace;
  font-size: 11px;
  color: #94a3b8;
  margin: 0 0 12px 0;
  font-weight: 700;
  letter-spacing: 0.5px;
`;

const TranscriptMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TranscriptMessage = styled.div`
  background: ${(props) =>
    props.role === "user"
      ? "rgba(59, 130, 246, 0.1)"
      : "rgba(16, 185, 129, 0.1)"};
  border-left: 3px solid
    ${(props) => (props.role === "user" ? "#3b82f6" : "#10b981")};
  padding: 8px 12px;
  border-radius: 0 6px 6px 0;
`;

const MessageMeta = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const MessageRole = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 9px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
`;

const MessageTime = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 9px;
  color: #475569;
`;

const MessageContent = styled.div`
  font-size: 11px;
  line-height: 1.4;
  color: #e2e8f0;
`;

const VendorLogs = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const VendorLogEntry = styled.div`
  background: ${(props) =>
    props.isPortal ? "rgba(16, 185, 129, 0.1)" : "rgba(30, 41, 59, 0.5)"};
  border-radius: 4px;
  padding: 8px;
  border-left: 2px solid ${(props) => (props.isPortal ? "#10b981" : "#f59e0b")};

  &:hover {
    background: rgba(59, 130, 246, 0.05);
  }
`;

const LogTime = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 9px;
  color: #64748b;
  margin-bottom: 2px;
`;

const LogAction = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  font-weight: 600;
  color: ${(props) => (props.isPortal ? "#10b981" : "#f59e0b")};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const LogDetails = styled.div`
  font-size: 10px;
  color: #94a3b8;
  line-height: 1.3;
`;

const PortalBadge = styled.span`
  background: linear-gradient(90deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  font-size: 8px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  letter-spacing: 0.5px;
`;

const SecurityGate = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  text-align: center;
  padding: 24px;
`;

const GateLock = styled.div`
  font-size: 48px;
  line-height: 1;
  margin-bottom: 16px;
  filter: grayscale(0.2);
`;

const GateTitle = styled.h1`
  font-family: "IBM Plex Mono", monospace;
  font-size: 32px;
  font-weight: 700;
  color: #ef4444;
  margin-bottom: 16px;
  letter-spacing: 2px;
  text-shadow: 0 0 20px rgba(239, 68, 68, 0.5);
`;

const GateMessage = styled.div`
  font-size: 18px;
  color: #94a3b8;
  margin-bottom: 24px;
`;

const GateCode = styled.pre`
  font-family: "IBM Plex Mono", monospace;
  font-size: 13px;
  color: #e2e8f0;
  background: rgba(30, 41, 59, 0.8);
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid #334155;
  max-width: 90vw;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
`;

const GateDetails = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: #64748b;
  margin-top: 100px;
  font-size: 16px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  margin-top: 100px;
`;

const ErrorCode = styled.div`
  font-family: "IBM Plex Mono", monospace;
  color: #ef4444;
  font-size: 14px;
  margin-bottom: 20px;
`;

const RetryButton = styled.button`
  padding: 12px 24px;
  background: #1e293b;
  border: 1px solid #475569;
  color: #e2e8f0;
  cursor: pointer;
  font-family: "IBM Plex Mono", monospace;
  font-size: 12px;
  border-radius: 6px;

  &:hover {
    background: #334155;
    border-color: #3b82f6;
  }
`;

// Reuse existing styled components for metrics
const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const MetricValue = styled.span`
  font-family: "IBM Plex Mono", monospace;
  font-weight: 700;
  font-size: 14px;
  color: ${(props) =>
    props.success
      ? "#10b981"
      : props.warning
      ? "#f59e0b"
      : props.error
      ? "#ef4444"
      : "#e2e8f0"};
`;

const FeeBreakdown = styled.div`
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  color: #64748b;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #334155;
`;

const JobList = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const JobItem = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid #334155;
  background: ${(props) =>
    props.urgent ? "rgba(239, 68, 68, 0.05)" : "transparent"};

  &:last-child {
    border-bottom: none;
  }
`;

const JobDescription = styled.div`
  font-size: 11px;
  color: #e2e8f0;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const JobMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const JobBadge = styled.span`
  font-family: "IBM Plex Mono", monospace;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 3px;
  background: ${(props) => {
    if (props.urgent) return "#ef4444";
    if (props.error) return "#dc2626";
    return "#374151";
  }};
  color: white;
`;

const InterventionButton = styled.button`
  font-family: "IBM Plex Mono", monospace;
  font-size: 9px;
  padding: 2px 6px;
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  font-weight: 600;

  &:hover {
    background: #d97706;
  }
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 12px;
  font-family: "IBM Plex Mono", monospace;
`;

export default OpsDashboardEnhanced;
